/**
 * @file rowMapping.js
 * @description Composant représentant une ligne de mapping dans l'éditeur de mapping.
 *
 * Ce composant permet de:
 * - Définir une correspondance entre un chemin (path) de sortie et une valeur d'entrée
 * - Choisir le type de valeur: dictionnaire, handlebars (variable), ou valeur personnalisée
 * - Naviguer dans les dictionnaires disponibles pour sélectionner des variables
 *
 * Architecture:
 * - RowInput: Sous-composant pour les champs de saisie avec gestion locale de l'état
 * - RowMapping: Composant principal gérant une ligne complète de mapping
 *
 * Types de valeurs supportés:
 * - DICTIONARY: Valeur provenant d'un dictionnaire (affichage du chemin lisible)
 * - HANDLEBARS: Variable handlebars libre (ex: {{{ json context.user }}})
 * - CUSTOM_VALUE/HARDCODED: Valeur en dur saisie par l'utilisateur
 */
import lod_ from "lodash";
import { useEffect, useState, useCallback } from "react";
import MDBox from "components/Basics/MDBox";
import { Icon, IconButton, TableCell, Tooltip } from "@mui/material";
import DictionaryMenu from "pages/settings/filters/DictionaryMenu";
import MDInput from "components/Basics/MDInput";
import { grey } from "@mui/material/colors";
import { getLabelPath, getDictionaryPath } from "helpers/dictionary";
import { useSelector } from "react-redux";
import { selectCurrentProfile } from "redux-react/reducers/profileReducer";

/**
 * Types de valeurs possibles pour une ligne de mapping
 * @constant
 */
export const TYPES = {
	/** Valeur sélectionnée depuis un dictionnaire */
	DICTIONARY: "DICTIONARY",
	/** Valeur personnalisée saisie manuellement */
	CUSTOM_VALUE: "CUSTOM_VALUE",
	/** Variable handlebars libre */
	HANDLEBARS: "HANDLEBARS",
	/** Alias pour CUSTOM_VALUE (rétrocompatibilité) */
	HARDCODED: "HARDCODED"
};

/**
 * Composant d'input contrôlé avec synchronisation de l'état local.
 *
 * Utilise un état local pour éviter les re-renders à chaque frappe,
 * et propage la valeur au parent uniquement sur blur (perte de focus).
 *
 * @param {Object} props
 * @param {string} props.value - Valeur initiale/contrôlée depuis le parent
 * @param {Function} props.onChange - Callback appelé sur blur avec la nouvelle valeur
 * @param {Object} props.rest - Props additionnelles passées à MDInput
 *
 */
const RowInput = ({ value: valueProps = "", onChange = () => {}, ...rest }) => {
	// État local pour éviter les re-renders du parent à chaque frappe
	const [value, setValue] = useState(valueProps);

	// Synchronise l'état local quand la prop change (ex: reset du formulaire)
	useEffect(() => {
		setValue(valueProps);
	}, [valueProps]);

	return (
		<MDInput
			fullWidth
			value={value}
			onChange={e => setValue(e.target.value)}
			// Propage la valeur au parent uniquement sur blur pour optimiser les performances
			onBlur={e => {
				onChange(e.target.value);
			}}
			{...rest}
		/>
	);
};

/**
 * Composant représentant une ligne de mapping.
 *
 * Structure visuelle (mode objet):
 * [Chemin de sortie] <-- [Valeur d'entrée] [Actions]
 *
 * Structure visuelle (mode valeur):
 * [Valeur d'entrée] [Actions]
 *
 * @param {Object} props
 * @param {boolean} props.mappingIsObject - Si true, affiche le champ path (mode objet)
 * @param {string|null} props.handlebarValue - Valeur handlebars associée (via mapObject)
 * @param {Object} props.config - Configuration (dictionnaires disponibles)
 * @param {string} props.path - Chemin de la propriété dans l'objet de mapping
 * @param {string|number|boolean} props.value - Valeur brute (peut être un UUID référençant mapObject)
 * @param {Function} props.onChangePath - Callback pour modifier le chemin
 * @param {Function} props.onChangeValue - Callback pour modifier la valeur (type, path, value)
 * @param {Function} props.deleteRow - Callback pour supprimer cette ligne
 */
const RowMapping = ({
	mappingIsObject,
	handlebarValue,
	config,
	path,
	value,
	onChangePath,
	onChangeValue,
	deleteRow,
	allPaths
}) => {
	const profile = useSelector(selectCurrentProfile);
	const inputDictionaries = config.dictionariesInput;
	const outputDictionaries = config.dictionariesOutput;

	// Vérifie si des dictionnaires sont disponibles pour la sélection
	const hasDictionariesInput = !lod_.isNil(inputDictionaries) && !lod_.isEmpty(inputDictionaries);
	const hasDictionariesOutput = !lod_.isNil(outputDictionaries) && !lod_.isEmpty(outputDictionaries);

	// Ancres pour les menus déroulants de sélection de dictionnaire
	const [anchorElInput, setAnchorElInput] = useState(null);
	const [anchorElOutput, setAnchorElOutput] = useState(null);

	// Type de variable détecté/sélectionné pour cette ligne
	// Détermine l'affichage et le comportement du champ valeur
	const [variableType, setVariableType] = useState(null);

	/**
	 * Nettoie une expression handlebars pour extraire le chemin de la variable.
	 *
	 * @example
	 * cleanHandlebar("{{{ json context.user.name }}}") // retourne "context.user.name"
	 * cleanHandlebar("{{ this }}") // retourne "this"
	 *
	 * @param {string} handlebarValue - Expression handlebars à nettoyer
	 * @returns {string} Chemin de la variable extrait
	 */
	const cleanHandlebar = useCallback(handlebarValue => {
		let cleanValue = handlebarValue.replaceAll("{{{", "");
		cleanValue = cleanValue.replaceAll("{{", "");
		cleanValue = cleanValue.replaceAll("}}}", "");
		cleanValue = cleanValue.replaceAll("}}", "");
		cleanValue = cleanValue.trim();

		// Gère le cas des helpers handlebars: "{{{ json path }}}" -> extrait "path"
		const parts = cleanValue.split(" ");
		if (parts.length === 1) {
			return cleanValue?.trim();
		} else if (parts.length > 1) {
			// Le helper est en position 0, le chemin en position 1
			return parts[1]?.trim();
		} else {
			return cleanValue?.trim();
		}
	}, []);

	/**
	 * Gestionnaire de changement du chemin de sortie.
	 * Nettoie la saisie (espaces, points multiples) avant de propager.
	 *
	 * @param {string} inputValue - Nouvelle valeur du chemin
	 */
	const handlePathChange = useCallback(
		inputValue => {
			let cleanedValue = inputValue.replaceAll(" ", "");
			// Remplace les points multiples par un seul point
			cleanedValue = cleanedValue.replaceAll(/\.{2,}/g, ".");

			// Supprime le point final si présent
			if (cleanedValue.endsWith(".")) {
				cleanedValue = cleanedValue.slice(0, -1);
			}

			onChangePath(path, cleanedValue, value);
		},
		[path, value, onChangePath]
	);

	/**
	 * Retourne la valeur à afficher dans le champ input selon le type.
	 *
	 * - CUSTOM_VALUE: Affiche la valeur brute
	 * - HANDLEBARS: Affiche l'expression handlebars (depuis mapObject)
	 *
	 * @returns {string|number|boolean} Valeur à afficher
	 */
	const getRowInputValue = () => {
		switch (variableType) {
			case TYPES.CUSTOM_VALUE:
				return value;
			case TYPES.HANDLEBARS: {
				if (!lod_.isNil(handlebarValue)) {
					return handlebarValue;
				}
				return value;
			}
			default:
				return value;
		}
	};

	/**
	 * Effet pour détecter automatiquement le type de valeur.
	 *
	 * Ordre de priorité:
	 * 1. Si pas une string -> CUSTOM_VALUE
	 * 2. Si expression handlebars et trouvée dans dictionnaire -> DICTIONARY
	 * 3. Si expression handlebars -> HANDLEBARS
	 * 4. Sinon -> CUSTOM_VALUE
	 */
	useEffect(() => {
		// Les valeurs non-string sont des valeurs personnalisées
		if (typeof value !== "string") {
			setVariableType(TYPES.CUSTOM_VALUE);
			return;
		}

		// Utilise handlebarValue s'il existe, sinon value
		// NOTE: On ne mute plus le paramètre `value` (mauvaise pratique React)
		const valueToCheck = !lod_.isNil(handlebarValue) ? handlebarValue : value;

		// Vérifie si c'est une expression handlebars
		const isHandlebars =
			(valueToCheck.startsWith("{{{") && valueToCheck.endsWith("}}}")) ||
			(valueToCheck.startsWith("{{") && valueToCheck.endsWith("}}")) ||
			!lod_.isNil(handlebarValue);

		if (isHandlebars) {
			// Tente de trouver dans le dictionnaire
			try {
				const cleanValue = cleanHandlebar(valueToCheck);
				const dictionaryPath = cleanValue.replaceAll(".", ".items.");
				if (
					lod_.has(inputDictionaries, dictionaryPath) ||
					lod_.has(outputDictionaries, dictionaryPath)
				) {
					setVariableType(TYPES.DICTIONARY);
					return;
				}
			} catch {
				// Ignore les erreurs de parsing
			}
			setVariableType(TYPES.HANDLEBARS);
			return;
		}

		setVariableType(TYPES.CUSTOM_VALUE);
	}, [value, handlebarValue, inputDictionaries, outputDictionaries, cleanHandlebar]);

	if (!variableType) {
		return null;
	}

	return (
		<MDBox display="flex" flexDirection="row" alignItems="center" style={{ width: "100%" }}>
			{mappingIsObject && (
				<TableCell style={{ border: "none" }} width="50%">
					<MDBox
						display="flex"
						flexDirection="row"
						justifyContent="space-between"
						alignItems="stretch"
					>
						{/* Champ de saisie du chemin de sortie */}
						{lod_.has(outputDictionaries, getDictionaryPath(path)) && (
							<MDBox
								flex="1"
								className="boxInputStyle"
								borderRadius="md"
								style={{
									borderRadius: "0.375rem 0 0 0.375rem"
								}}
							>
								{getLabelPath(outputDictionaries, path, profile.language, "None")}
							</MDBox>
						)}
						{!lod_.has(outputDictionaries, getDictionaryPath(path)) && (
							<RowInput
								value={path}
								onChange={handlePathChange}
								sx={{
									"& .MuiOutlinedInput-root": {
										height: "100%"
									}
								}}
								InputProps={{
									sx: {
										borderRadius: "0.375rem 0 0 0.375rem"
									}
								}}
							/>
						)}
						<MDBox
							className="endButtonboxInputStyle"
							display="flex"
							justifyContent="center"
							alignItems="center"
							bgColor={hasDictionariesOutput ? "light" : grey[300]}
							onClick={e => {
								if (hasDictionariesOutput === true) {
									setAnchorElOutput(e.target);
								}
							}}
						>
							<Icon fontSize="small">menu</Icon>
						</MDBox>
						{/* Menu de sélection depuis le dictionnaire pour le chemin */}
						<DictionaryMenu
							placement="right"
							dictionary={outputDictionaries}
							anchorEl={anchorElOutput}
							handleInsertText={e => {
								setAnchorElOutput(null);
								// If already exists; do not add again
								if (allPaths.includes(e)) {
									return;
								}
								handlePathChange(e);
							}}
							handleClose={() => setAnchorElOutput(null)}
						/>
					</MDBox>
				</TableCell>
			)}
			{/* Mid cell */}
			{mappingIsObject && (
				<TableCell style={{ border: "none" }}>
					<MDBox display="flex" flexDirection="row" justifyContent="center" alignItems="center">
						<Icon color="success" fontSize="medium">
							arrow_backward
						</Icon>
					</MDBox>
				</TableCell>
			)}
			{/* ==================== SECTION VALEUR D'ENTRÉE ==================== */}
			<TableCell style={{ border: "none" }} width={mappingIsObject ? "50%" : "100%"}>
				<MDBox
					display="flex"
					flexDirection="row"
					justifyContent="space-between"
					alignItems="stretch"
				>
					{/* Champ texte pour HANDLEBARS et CUSTOM_VALUE */}
					{(variableType === TYPES.HANDLEBARS || variableType === TYPES.CUSTOM_VALUE) && (
						<RowInput
							value={getRowInputValue()}
							onChange={e => {
								onChangeValue(variableType, path, e);
							}}
							sx={{
								"& .MuiOutlinedInput-root": {
									height: "100%"
								}
							}}
							InputProps={{
								sx: {
									borderRadius: "0.375rem 0 0 0.375rem"
								}
							}}
						/>
					)}
					{/* Affichage du chemin lisible pour DICTIONARY */}
					{variableType === TYPES.DICTIONARY && (
						<MDBox
							flex="1"
							className="boxInputStyle"
							borderRadius="md"
							style={{
								borderRadius: "0.375rem 0 0 0.375rem"
							}}
						>
							{getLabelPath(
								inputDictionaries,
								cleanHandlebar(handlebarValue),
								profile.language,
								"None"
							)}
						</MDBox>
					)}

					{/* ========== BOUTONS DE SÉLECTION DU TYPE ========== */}

					{/* Bouton: Mode Variable (Handlebars) */}
					<Tooltip title="Variables" placement="top">
						<MDBox
							className="endButtonboxInputStyle"
							display="flex"
							justifyContent="center"
							alignItems="center"
							bgColor={
								[TYPES.HANDLEBARS, TYPES.DICTIONARY].includes(variableType) ? "dark" : "light"
							}
							style={{
								borderRadius: "0"
							}}
							onClick={() => {
								setVariableType(TYPES.HANDLEBARS);
								onChangeValue(TYPES.HANDLEBARS, path, "");
							}}
						>
							<Icon
								style={{
									color: [TYPES.HANDLEBARS, TYPES.DICTIONARY].includes(variableType)
										? grey[50]
										: "inherit"
								}}
								fontSize="small"
							>
								construction
							</Icon>
						</MDBox>
					</Tooltip>

					{/* Bouton: Mode Valeur personnalisée */}
					<Tooltip title="Valeur personnalisée" placement="top">
						<MDBox
							className="endButtonboxInputStyle"
							display="flex"
							justifyContent="center"
							alignItems="center"
							bgColor={variableType === TYPES.CUSTOM_VALUE ? "dark" : "light"}
							style={{
								borderRadius: "0"
							}}
							onClick={() => {
								setVariableType(TYPES.CUSTOM_VALUE);
								onChangeValue(TYPES.CUSTOM_VALUE, path, "");
							}}
						>
							<Icon
								style={{
									color: variableType === TYPES.CUSTOM_VALUE ? grey[50] : "inherit"
								}}
								fontSize="small"
							>
								edit
							</Icon>
						</MDBox>
					</Tooltip>
					{/* Bouton: Ouvrir le menu de sélection depuis dictionnaire */}
					<MDBox
						className="endButtonboxInputStyle"
						display="flex"
						justifyContent="center"
						alignItems="center"
						bgColor={hasDictionariesInput ? "light" : grey[300]}
						onClick={e => {
							if (hasDictionariesInput === true) {
								setAnchorElInput(e.target);
							}
						}}
					>
						<Icon fontSize="small">menu</Icon>
					</MDBox>

					{/* Menu déroulant de sélection depuis le dictionnaire pour la valeur */}
					<DictionaryMenu
						placement="right"
						dictionary={inputDictionaries}
						anchorEl={anchorElInput}
						handleInsertText={e => {
							setAnchorElInput(null);
							onChangeValue(TYPES.DICTIONARY, path, `{{{ json ${e} }}}`);
						}}
						handleClose={() => setAnchorElInput(null)}
					/>
				</MDBox>
			</TableCell>

			{/* Bouton de suppression de la ligne (uniquement en mode objet) */}
			{mappingIsObject === true && (
				<IconButton onClick={() => deleteRow(path)}>
					<Icon>delete</Icon>
				</IconButton>
			)}
		</MDBox>
	);
};

export default RowMapping;
